import { useRef } from "react";
import { useTransform, useScroll, useSpring } from "framer-motion";
import {
  MotionBox as Box,
  MotionFlex as Flex,
  staggeredAppear,
  viewAppearDefault,
} from "utils/motion";
import Image from "components/base/Image";
import Text from "components/base/Text";
import Trans from "components/base/Trans";
import Button from "components/base/Button";

const styles = {
  container: {
    position: "relative",
    width: "100%",
    minHeight: "100vh",
  },
  backgroundContent: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 0,
  },
  backgroundImage: {
    width: "100%",
    height: "100%",
  },
  foregroundContent: {
    position: "fixed",
    width: "100%",
    backgroundColor: "black",
    mixBlendMode: "multiply",
    minHeight: "100%",
    display: "flex",
    justifyContent: "center",
  },
  knockoutText: {
    color: "white",
    fontWeight: 900,
    textAlign: "center",
    textTransform: "uppercase",
    lineHeight: 1,
  },
};

export default function HomeHeroHeader3({
  image,
  title,
  supTitle,
  subtitle,
  cta,
  tags,
  props,
}) {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "200vh"],
  });
  const springScroll = useSpring(scrollYProgress, {
    stiffness: 176,
    damping: 26,
  });
  const opacityLettersMask = useTransform(springScroll, [0, 0.001], [1, 0]);
  const opacityTexts = useTransform(springScroll, [0, 0.01], [1, 0]);
  const zIndexTexts = useTransform(springScroll, [0, 0.01], [0, -1]);

  const opacityImg = useTransform(
    springScroll,
    [0.5, 0.8, 0.95, 1],
    [1, 0.2, 0.2, 0],
  );
  const blur = useTransform(
    springScroll,
    [0.5, 0.8, 0.9, 1],
    ["blur(0px)", "blur(2px)", "blur(10px)", "blur(50px)"],
  );
  const yImg = useTransform(springScroll, [0.4, 1], ["0%", "-20vh"]);
  const zIndexImg = useTransform(springScroll, [0.999, 1], [0, -1]);

  const scaleMask = useTransform(springScroll, [0, 0.4], [1, 70]);
  const xMask = useTransform(springScroll, [0, 0.4], ["0", "-300vw"]);
  const opacityMask = useTransform(springScroll, [0.4, 0.45], [1, 0]);
  const zIndexMask = useTransform(springScroll, [0.999, 1], [0, -1]);
  const zIndexIOSMask = useTransform(springScroll, [0.999, 1], [2, -1]);
  const yIOSMask = useTransform(springScroll, [0, 0.2], ["0px", "40px"]);

  return (
    <Box
      ref={ref}
      position="relative"
      overflow="hidden"
      w="100%"
      h="100%"
      bg="transparent"
      as="section"
      {...props}
    >
      <Box
        h="40px"
        bg="black.900"
        w="100%"
        position="fixed"
        bottom="-20px"
        style={{
          opacity: opacityMask,
          zIndex: zIndexIOSMask,
          y: yIOSMask,
        }}
      />

      <Box height="200vh" minH="600px" position="relative" overflow="hidden">
        {/* 1. BACKGROUND LAYER */}
        <Box style={{ ...styles.backgroundContent, zIndex: zIndexImg }}>
          <Box style={styles.backgroundImage}>
            <Box
              height="205vh"
              minH="600px"
              position="relative"
              overflow="hidden"
            >
              {/** HELI IMAGE */}
              <Box
                position="fixed"
                top={0}
                width="100%"
                height="100%"
                style={{
                  opacity: opacityImg,
                  y: yImg,
                  filter: blur,
                }}
              >
                <Image
                  priority
                  src={image}
                  alt="hx50"
                  height="150vh"
                  objectFit="cover"
                />
              </Box>
              {/** WHITE LETTERS */}
              <Box
                position="absolute"
                w="100%"
                h="100%"
                top={0}
                bg="white"
                style={{ opacity: opacityLettersMask }}
              />
            </Box>
          </Box>
        </Box>

        {/* 2. FOREGROUND LAYER (THE MASK) */}
        <Box
          position="fixed"
          top={0}
          style={{
            ...styles.foregroundContent,
            zIndex: zIndexMask,
            opacity: opacityMask,
          }}
        >
          <Box align="center">
            <Box h={["25vh", null, "20vh", "30vh"]} minH="120px" />
            <Text
              type="headline_sm"
              color="primary"
              style={{
                lineHeight: 1,
                opacity: opacityTexts,
                zIndex: zIndexTexts,
              }}
            >
              {supTitle}
            </Text>
            <Text
              type="headline_xxl"
              letterSpacing={["12px", null, "16px"]}
              fontSize={["56px", "80px", "120px"]}
              py={["16px", "16px", "lg"]}
              style={{ ...styles.knockoutText, scale: scaleMask, x: xMask }}
            >
              {title}
            </Text>
            <Text
              type="title_lg"
              color="white"
              style={{
                lineHeight: 1,
                opacity: opacityTexts,
                zIndex: zIndexTexts,
              }}
            >
              {subtitle}
            </Text>

            <Box
              position="fixed"
              w="100%"
              bottom="xxl"
              left={0}
              style={{ opacity: opacityTexts, zIndex: zIndexTexts }}
            >
              <Box
                variants={staggeredAppear}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
              >
                <Flex
                  maxW="lg"
                  justify="center"
                  mb="xl"
                  wrap="wrap"
                  display={["none", null, "flex"]}
                >
                  {tags.map((item, index) => (
                    <Flex
                      key={index}
                      variants={viewAppearDefault}
                      w={["90%", "180px", "260px"]}
                      px="20px"
                      align="center"
                      justify={
                        index === 0
                          ? "flex-end"
                          : index === tags.length - 1
                            ? "flex-start"
                            : "center"
                      }
                      borderRight={
                        index !== tags.length - 1
                          ? [
                              "0px solid",
                              "0px solid",
                              "1px solid rgba(255,255,255, 0.4)",
                            ]
                          : null
                      }
                    >
                      <Text
                        type="title_md"
                        fontWeight="500"
                        color="white"
                        textAlign={
                          index === 0
                            ? "right"
                            : index === tags.length - 1
                              ? "left"
                              : "center"
                        }
                        p="xs"
                      >
                        <Trans id={item} />
                      </Text>
                    </Flex>
                  ))}
                </Flex>
                <Flex
                  maxW="80%"
                  justify="center"
                  mb="md"
                  wrap="wrap"
                  direction="column"
                  display={["flex", null, "none"]}
                >
                  {tags.map((item, index) => (
                    <Box key={index} variants={viewAppearDefault} px="md">
                      <Text
                        type="title_md"
                        fontWeight="500"
                        color="white"
                        textAlign="center"
                        p="xs"
                      >
                        <Trans id={item} />
                      </Text>
                      {tags.length - 1 !== index ? (
                        <Box
                          h="1px"
                          w="50%"
                          bg="white"
                          opacity={0.4}
                          my="8px"
                        />
                      ) : null}
                    </Box>
                  ))}
                </Flex>
              </Box>
              <Button
                w={["90%", "180px", "220px"]}
                onClick={() =>
                  document
                    .getElementById("register")
                    .scrollIntoView({ behavior: "smooth" })
                }
                variant="solid"
                size="regular"
              >
                {cta}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
